import React from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    Box,
    Typography,
    Stack,
    Chip,
    Divider,
    IconButton,
    Avatar,
    Skeleton,
} from "@mui/material";

import CloseIcon from "@mui/icons-material/Close";
import { useLeadDetails } from "../../hooks/useLeadDetails";
import LeadProfileSections, { SectionTitle, DetailRow } from "./LeadProfileSections";

const STATUS_COLOR = {
    "Em conversa": "info",
    "Sem resposta": "warning",
    Fechado: "success",
};

const PRIORITY_COLOR = {
    Alta: "error",
    Média: "warning",
    Baixa: "default",
};

function initials(name = "") {
    return name.split(" ").filter(Boolean).slice(0, 2).map((p) => p[0].toUpperCase()).join("");
}

export default function LeadDetailsDialog({ open, onClose, lead }) {
    const { details, loading } = useLeadDetails(open && lead ? lead.id : null);

    if (!lead) return null;

    const lastAt = lead.lastMessageAt
        ? new Date(lead.lastMessageAt).toLocaleString("pt-BR", {
              day: "2-digit", month: "2-digit", year: "numeric",
              hour: "2-digit", minute: "2-digit",
          })
        : null;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pb: 1 }}>
                <Stack direction="row" alignItems="center" spacing={1.5}>
                    <Avatar sx={{ width: 40, height: 40, bgcolor: "rgba(33,49,89,0.16)", color: "var(--cise-blue)", fontWeight: 900 }}>
                        {initials(lead.name)}
                    </Avatar>
                    <Box>
                        <Typography sx={{ fontWeight: 900, fontSize: 16, lineHeight: 1.1 }}>{lead.name}</Typography>
                        <Typography sx={{ fontSize: 12, color: "var(--cise-text-muted)" }}>{lead.phone}</Typography>
                    </Box>
                </Stack>
                <IconButton onClick={onClose} size="small">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent dividers sx={{ bgcolor: "var(--bg)" }}>
                {/* Chips de status */}
                <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ gap: 0.8, mb: 2 }}>
                    <Chip label={lead.status} color={STATUS_COLOR[lead.status] || "default"} size="small" sx={{ fontWeight: 700 }} />
                    <Chip label={`Prioridade ${lead.priority}`} color={PRIORITY_COLOR[lead.priority] || "default"} size="small" sx={{ fontWeight: 700 }} />
                    <Chip label={lead.stage} variant="outlined" size="small" sx={{ fontWeight: 700 }} />
                </Stack>

                {/* Atendimento */}
                <SectionTitle>Atendimento</SectionTitle>
                <Box sx={{ bgcolor: "var(--branco)", borderRadius: 1, border: "1px solid var(--border)", px: 2, py: 1 }}>
                    <DetailRow label="Agente responsável" value={lead.owner} />
                    <DetailRow label="Etapa no funil" value={lead.stage} />
                    <DetailRow label="Última interação" value={lastAt} />
                    <DetailRow label="Atendimento" value={lead.standby ? "Encerrado (standby)" : "Ativo"} />
                </Box>

                {loading ? (
                    <Box sx={{ pt: 2 }}>
                        {[90, 70, 100, 60].map((w, i) => (
                            <Skeleton key={i} variant="text" width={`${w}%`} height={20} sx={{ mb: 0.5 }} />
                        ))}
                    </Box>
                ) : details ? (
                    <>
                        <LeadProfileSections details={details} phone={lead.phone} />
                        <Divider sx={{ mt: 2.5 }} />
                    </>
                ) : (
                    <Typography sx={{ fontSize: 12, color: "var(--cise-text-muted)", pt: 2 }}>
                        O atendimento ainda não gerou dados de perfil.
                    </Typography>
                )}

                <Box sx={{ pt: 1 }}>
                    <DetailRow label="WhatsApp ID" value={lead.id} />
                </Box>
            </DialogContent>
        </Dialog>
    );
}
